import React, { useEffect } from 'react';
import { CameraIcon, GlobeAltIcon, XMarkIcon } from '@heroicons/react/24/outline';

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(16, 18, 24, 0.48)',
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  zIndex: 9999
};

const modalStyle = {
  width: 'min(880px, 100%)',
  maxHeight: '90vh',
  overflow: 'auto',
  background: 'var(--Surface-Primary)',
  border: '1px solid var(--Stroke-Light)',
  borderRadius: 'var(--radius-xl)',
  boxShadow: 'var(--shadow-frame-big)'
};

const closeButtonStyle = {
  width: '40px',
  height: '40px',
  borderRadius: '999px',
  border: '1px solid var(--Stroke-Light)',
  background: 'var(--Surface-Primary)',
  color: 'var(--Text-Primary)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer'
};

const textSectionStyle = {
  background: 'var(--Surface-Secondary)',
  border: '1px solid var(--Stroke-Light)',
  borderRadius: 'var(--radius-l)',
  padding: 'var(--spacing-l)'
};

const linkPillStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 14px',
  borderRadius: '999px',
  border: '1px solid var(--Stroke-Light)',
  background: 'var(--Surface-Primary)',
  color: 'var(--Text-Primary)',
  textDecoration: 'none',
  fontSize: 'var(--font-size-body4)',
  fontWeight: 'var(--font-weight-medium)'
};

const JewellerProfileModal = ({ jeweller, onClose }) => {
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (!jeweller) {
    return null;
  }

  const fallbackBio =
    jeweller.introParagraph ||
    jeweller.bio ||
    'More about this jeweller will appear here soon.';

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(event) => event.stopPropagation()}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(280px, 340px) minmax(0, 1fr)',
            gap: 'var(--spacing-xl)',
            padding: 'var(--spacing-xl)'
          }}
        >
          <div>
            <div
              style={{
                height: '360px',
                borderRadius: 'var(--radius-xl)',
                overflow: 'hidden',
                background: 'var(--Grey-100)',
                marginBottom: 'var(--spacing-l)'
              }}
            >
              {jeweller.profilePhoto ? (
                <img
                  src={jeweller.profilePhoto}
                  alt={jeweller.companyName}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block'
                  }}
                />
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--Text-Tertiary)'
                  }}
                >
                  No profile image
                </div>
                )}
              </div>

              {(jeweller.website || jeweller.instagram) && (
                <section style={{ ...textSectionStyle, marginTop: 'var(--spacing-l)' }}>
                  <h3
                    style={{
                      margin: '0 0 var(--spacing-xs) 0',
                      color: 'var(--Text-Primary)',
                      fontSize: 'var(--font-size-subtitle2)',
                      fontWeight: 'var(--font-weight-subtitle2)'
                    }}
                  >
                    Find them online
                  </h3>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 'var(--spacing-s)'
                    }}
                  >
                    {jeweller.website && (
                      <a
                        href={jeweller.website}
                        target="_blank"
                        rel="noreferrer"
                        style={linkPillStyle}
                      >
                        <GlobeAltIcon style={{ width: '18px', height: '18px' }} />
                        <span>Website</span>
                      </a>
                    )}
                    {jeweller.instagram && (
                      <a
                        href={jeweller.instagram}
                        target="_blank"
                        rel="noreferrer"
                        style={linkPillStyle}
                      >
                        <CameraIcon style={{ width: '18px', height: '18px' }} />
                        <span>Instagram</span>
                      </a>
                    )}
                  </div>
                </section>
              )}
          </div>

          <div style={{ minWidth: 0 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 'var(--spacing-l)',
                marginBottom: 'var(--spacing-l)'
              }}
            >
              <div>
                <h2
                  style={{
                    margin: '0 0 var(--spacing-xxs) 0',
                    color: 'var(--Text-Primary)',
                    fontSize: 'var(--font-size-h4)',
                    fontWeight: 'var(--font-weight-h4)'
                  }}
                >
                  {jeweller.companyName}
                </h2>
                <p
                  style={{
                    margin: '0 0 var(--spacing-xxs) 0',
                    color: 'var(--Text-Secondary)',
                    fontSize: 'var(--font-size-body2)',
                    fontWeight: 'var(--font-weight-medium)'
                  }}
                >
                  {jeweller.fullName}
                </p>
                {jeweller.location && (
                  <p
                    style={{
                      margin: 0,
                      color: 'var(--Text-Tertiary)',
                      fontSize: 'var(--font-size-body4)'
                    }}
                  >
                    {jeweller.location}
                  </p>
                )}
              </div>

              <button type="button" style={closeButtonStyle} onClick={onClose}>
                <XMarkIcon style={{ width: '20px', height: '20px' }} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-m)' }}>
              {jeweller.introParagraph && (
                <section style={textSectionStyle}>
                  <h3
                    style={{
                      margin: '0 0 var(--spacing-xs) 0',
                      color: 'var(--Text-Primary)',
                      fontSize: 'var(--font-size-subtitle2)',
                      fontWeight: 'var(--font-weight-subtitle2)'
                    }}
                  >
                    About the business
                  </h3>
                  <p
                    style={{
                      margin: 0,
                      color: 'var(--Text-Secondary)',
                      fontSize: 'var(--font-size-body3)',
                      lineHeight: '1.7'
                    }}
                  >
                    {jeweller.introParagraph}
                  </p>
                </section>
              )}

              {jeweller.bio ? (
                <section style={textSectionStyle}>
                  <h3
                    style={{
                      margin: '0 0 var(--spacing-xs) 0',
                      color: 'var(--Text-Primary)',
                      fontSize: 'var(--font-size-subtitle2)',
                      fontWeight: 'var(--font-weight-subtitle2)'
                    }}
                  >
                    About the jeweller
                  </h3>
                  <p
                    style={{
                      margin: 0,
                      color: 'var(--Text-Secondary)',
                      fontSize: 'var(--font-size-body3)',
                      lineHeight: '1.7'
                    }}
                  >
                    {jeweller.bio}
                  </p>
                </section>
              ) : !jeweller.introParagraph ? (
                <section style={textSectionStyle}>
                  <h3
                    style={{
                      margin: '0 0 var(--spacing-xs) 0',
                      color: 'var(--Text-Primary)',
                      fontSize: 'var(--font-size-subtitle2)',
                      fontWeight: 'var(--font-weight-subtitle2)'
                    }}
                  >
                    Profile overview
                  </h3>
                  <p
                    style={{
                      margin: 0,
                      color: 'var(--Text-Secondary)',
                      fontSize: 'var(--font-size-body3)',
                      lineHeight: '1.7'
                    }}
                  >
                    {fallbackBio}
                  </p>
                </section>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JewellerProfileModal;
